import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import type { DailyTotals } from '../../services/storeAuditReportsService';
import type { Store as StoreType } from '../../types/services/auth';
import { formatCurrency } from '../../utils/formatUtils';
import { getStoreConfigurationFlags } from './utils/legacyAuditStoreConfiguration';
import LegacyReconciliationLottoPosTile from './LegacyReconciliationLottoPosTile';
import LegacyReconciliationSummaryTile from './LegacyReconciliationSummaryTile';
import './LegacyReconciliationSummaryTiles.css';

type LegacyReconciliationSummaryTilesProps = {
  totals: DailyTotals | undefined;
  store?: StoreType | null;
};

const LegacyReconciliationSummaryTiles: React.FC<LegacyReconciliationSummaryTilesProps> = ({
  totals,
  store,
}) => {
  const flags = useMemo(() => getStoreConfigurationFlags(store), [store]);

  const scratcherTile = useMemo(() => {
    const scratcherRevenueInAudit = totals?.audit?.total_revenue ?? 0;
    const scratcherSoldQtyInAudit = totals?.audit?.total_sold ?? 0;
    const scratcherRevenueInPOS = totals?.pos?.scratcher_sales ?? 0;
    const scratcherSoldQtyInPOS = totals?.pos?.scratcher_count ?? 0;
    const scratcherRevenueVariance = totals?.reconciliation_variance?.scratcher?.revenue_variance ?? 0;
    const scratcherQuantityVariance = totals?.reconciliation_variance?.scratcher?.quantity_variance ?? 0;

    if (flags.isPOSConnectedOnly || flags.isBothConnected) {
      const hasNoVariance = scratcherRevenueVariance === 0 && scratcherQuantityVariance === 0;

      let badge: 'MATCHED' | 'DEFICIT' | 'SURPLUS' = 'MATCHED';
      if (scratcherRevenueVariance > 0) {
        badge = 'SURPLUS';
      } else if (scratcherRevenueVariance < 0 || scratcherQuantityVariance !== 0) {
        badge = 'DEFICIT';
      }

      const shouldShowMissingTickets = badge !== 'SURPLUS' && scratcherQuantityVariance !== 0;
      const quantityAbs = Math.abs(Number(scratcherQuantityVariance) || 0);

      return {
        mode: 'lottoPos' as const,
        title: 'SCRATCHER SALES',
        badge,
        value: hasNoVariance ? <CheckCircle sx={{ fontSize: 32, color: '#34C759' }} /> : formatCurrency(Number(scratcherRevenueVariance) || 0),
        subtext: !shouldShowMissingTickets ? undefined : (
          <Box sx={{ color: '#A0AEC0 !important', fontStyle: 'italic', fontSize: '13px', lineHeight: 1.1 }}>
            {quantityAbs} {quantityAbs === 1 ? 'ticket' : 'tickets'} missing
          </Box>
        ),
        footer: `Total: ${formatCurrency(Number(scratcherRevenueInPOS) || 0)} (${Number(scratcherSoldQtyInPOS) || 0})`,
        extraClasses: undefined as string | undefined,
        valueClassName: undefined as string | undefined,
      };
    }

    return {
      mode: 'summary' as const,
      title: 'SCRATCHER SALES',
      value: formatCurrency(Number(scratcherRevenueInAudit) || 0),
      subtext: `${Number(scratcherSoldQtyInAudit) || 0} ${Number(scratcherSoldQtyInAudit) === 1 ? 'ticket' : 'tickets'}`,
      footer: 'Reported by Audit',
      extraClasses: undefined as string | undefined,
      valueClassName: undefined as string | undefined,
    };
  }, [flags.isBothConnected, flags.isPOSConnectedOnly, totals]);

  const lottoTile = useMemo(() => {
    const lottoSalesInLotto = totals?.lotto?.sales ?? 0;
    const lottoSalesInPOS = totals?.pos?.lotto_sales ?? 0;
    const lottoSalesVariance = totals?.reconciliation_variance?.lotto_sales_variance ?? 0;

    if (flags.isBothConnected) {
      const hasNoVariance = lottoSalesVariance === 0;

      let badge: 'MATCHED' | 'DEFICIT' | 'SURPLUS' = 'MATCHED';
      if (lottoSalesVariance > 0) {
        badge = 'SURPLUS';
      } else if (lottoSalesVariance < 0) {
        badge = 'DEFICIT';
      }

      return {
        mode: 'lottoPos' as const,
        title: 'LOTTO SALES',
        badge,
        value: hasNoVariance ? <CheckCircle sx={{ fontSize: 32, color: '#34C759' }} /> : formatCurrency(Number(lottoSalesVariance) || 0),
        subtext: undefined as React.ReactNode | undefined,
        footer: `Total: ${formatCurrency(Number(lottoSalesInLotto) || 0)}`,
        extraClasses: undefined as string | undefined,
        valueClassName: undefined as string | undefined,
      };
    }

    if (flags.isPOSConnectedOnly) {
      return {
        mode: 'summary' as const,
        title: 'LOTTO SALES',
        value: formatCurrency(Number(lottoSalesInPOS) || 0),
        subtext: undefined as React.ReactNode | undefined,
        footer: 'Reported by POS',
        extraClasses: undefined as string | undefined,
        valueClassName: undefined as string | undefined,
      };
    }

    if (flags.isLottoConnectedOnly) {
      return {
        mode: 'summary' as const,
        title: 'LOTTO SALES',
        value: formatCurrency(Number(lottoSalesInLotto) || 0),
        subtext: undefined as React.ReactNode | undefined,
        footer: 'Reported by Lottery',
        extraClasses: undefined as string | undefined,
        valueClassName: undefined as string | undefined,
      };
    }

    if (flags.isBothNotConnected) {
      return {
        mode: 'summary' as const,
        title: 'LOTTO SALES',
        value: 'Match lotto sales reported by POS with sales in lottery portal.',
        subtext: undefined as React.ReactNode | undefined,
        footer: '',
        extraClasses: 'centered-content',
        valueClassName: 'hardcoded-value',
      };
    }

    return {
      mode: 'summary' as const,
      title: 'LOTTO SALES',
      value: formatCurrency(Number(lottoSalesInLotto) || 0),
      subtext: undefined as React.ReactNode | undefined,
      footer: 'Reported by Lottery',
      extraClasses: undefined as string | undefined,
      valueClassName: undefined as string | undefined,
    };
  }, [flags.isBothConnected, flags.isBothNotConnected, flags.isLottoConnectedOnly, flags.isPOSConnectedOnly, totals]);

  const payoutTile = useMemo(() => {
    const payoutsInLotto = totals?.lotto?.payouts_total ?? 0;
    const payoutsInPOS = totals?.pos?.payout ?? 0;
    const payoutsVariance = totals?.reconciliation_variance?.payout_variance ?? 0;
    const posFreeScratchersAmount = totals?.pos?.scratcher_free_tickets_paid_amount ?? 0;
    const posFreeScratchersCount = totals?.pos?.scratcher_free_tickets_paid_count ?? null;
    const lottoFreeScratchersAmount = totals?.lotto?.scratcher_free_tickets_paid_amount ?? 0;
    const lottoFreeScratchersCount = totals?.lotto?.scratcher_free_tickets_paid_count ?? null;

    const buildFreeScratchersSubtext = (amount: number, count: number | null) => (
      <Box sx={{ color: '#A0AEC0 !important', fontStyle: 'italic', fontSize: '12px', lineHeight: 1.1, whiteSpace: 'nowrap' }}>
        free scratchers {formatCurrency(Number(amount) || 0)}{count == null ? '' : `(${Number(count) || 0})`}
      </Box>
    );

    if (flags.isBothConnected) {
      const hasNoVariance = payoutsVariance === 0;

      let badge: 'MATCHED' | 'DEFICIT' | 'SURPLUS' = 'MATCHED';
      if (payoutsVariance > 0) {
        badge = 'SURPLUS';
      } else if (payoutsVariance < 0) {
        badge = 'DEFICIT';
      }

      return {
        mode: 'lottoPos' as const,
        title: 'PAYOUTS',
        badge,
        value: hasNoVariance ? <CheckCircle sx={{ fontSize: 32, color: '#34C759' }} /> : formatCurrency(Number(payoutsVariance) || 0),
        subtext: buildFreeScratchersSubtext(Number(posFreeScratchersAmount) || 0, posFreeScratchersCount),
        footer: `Total: ${formatCurrency(Number(payoutsInPOS) || 0)}`,
        extraClasses: undefined as string | undefined,
        valueClassName: undefined as string | undefined,
      };
    }

    if (flags.isPOSConnectedOnly) {
      return {
        mode: 'summary' as const,
        title: 'PAYOUTS',
        value: formatCurrency(Number(payoutsInPOS) || 0),
        subtext: buildFreeScratchersSubtext(Number(posFreeScratchersAmount) || 0, posFreeScratchersCount),
        footer: 'Reported by POS',
        extraClasses: undefined as string | undefined,
        valueClassName: undefined as string | undefined,
      };
    }

    if (flags.isLottoConnectedOnly) {
      return {
        mode: 'summary' as const,
        title: 'PAYOUTS',
        value: formatCurrency(Number(payoutsInLotto) || 0),
        subtext: buildFreeScratchersSubtext(Number(lottoFreeScratchersAmount) || 0, lottoFreeScratchersCount),
        footer: 'Reported by Lottery',
        extraClasses: undefined as string | undefined,
        valueClassName: undefined as string | undefined,
      };
    }

    if (flags.isBothNotConnected) {
      return {
        mode: 'summary' as const,
        title: 'PAYOUTS',
        value: 'Match payouts reported by POS with payouts in lottery portal.',
        footer: '',
        extraClasses: 'centered-content',
        valueClassName: 'hardcoded-value',
      };
    }

    return {
      mode: 'summary' as const,
      title: 'PAYOUTS',
      value: formatCurrency(Number(payoutsInLotto) || 0),
      footer: 'Reported by Lottery',
      extraClasses: undefined as string | undefined,
      valueClassName: undefined as string | undefined,
    };
  }, [flags.isBothConnected, flags.isBothNotConnected, flags.isLottoConnectedOnly, flags.isPOSConnectedOnly, totals]);

  const overallTile = useMemo(() => {
    if (!flags.isBothConnected) return null;
    const totalVariance = totals?.reconciliation_variance?.total_variance ?? 0;
    const hasNoVariance = totalVariance === 0;

    let badge: 'MATCHED' | 'DEFICIT' | 'SURPLUS' = 'MATCHED';
    if (totalVariance > 0) {
      badge = 'SURPLUS';
    } else if (totalVariance < 0) {
      badge = 'DEFICIT';
    }

    return {
      title: 'OVERALL',
      badge,
      value: hasNoVariance ? <CheckCircle sx={{ fontSize: 32, color: '#34C759' }} /> : formatCurrency(Number(totalVariance) || 0),
    };
  }, [flags.isBothConnected, totals]);

  return (
    <Box className="LegacyReconciliationSummaryTiles">
      <Box className="no-pos-type-cards">
        {scratcherTile.mode === 'lottoPos' ? (
          <LegacyReconciliationLottoPosTile
            title={scratcherTile.title}
            badge={scratcherTile.badge}
            value={scratcherTile.value}
            subtext={scratcherTile.subtext}
            footer={scratcherTile.footer}
          />
        ) : (
          <LegacyReconciliationSummaryTile
            title={scratcherTile.title}
            value={scratcherTile.value}
            subtext={scratcherTile.subtext}
            footer={scratcherTile.footer}
            cardClassName={scratcherTile.extraClasses || undefined}
            valueClassName={scratcherTile.valueClassName}
          />
        )}

        {lottoTile.mode === 'lottoPos' ? (
          <LegacyReconciliationLottoPosTile
            title={lottoTile.title}
            badge={lottoTile.badge}
            value={lottoTile.value}
            subtext={lottoTile.subtext}
            footer={lottoTile.footer}
          />
        ) : (
          <LegacyReconciliationSummaryTile
            title={lottoTile.title}
            value={lottoTile.value}
            subtext={lottoTile.subtext}
            footer={lottoTile.footer}
            cardClassName={lottoTile.extraClasses || undefined}
            valueClassName={lottoTile.valueClassName}
          />
        )}

        {payoutTile.mode === 'lottoPos' ? (
          <LegacyReconciliationLottoPosTile
            title={payoutTile.title}
            badge={payoutTile.badge}
            value={payoutTile.value}
            subtext={payoutTile.subtext}
            footer={payoutTile.footer}
          />
        ) : (
          <LegacyReconciliationSummaryTile
            title={payoutTile.title}
            value={payoutTile.value}
            subtext={payoutTile.subtext}
            footer={payoutTile.footer}
            cardClassName={payoutTile.extraClasses || undefined}
            valueClassName={payoutTile.valueClassName}
          />
        )}

        {overallTile ? (
          <LegacyReconciliationLottoPosTile
            title={overallTile.title}
            badge={overallTile.badge}
            value={overallTile.value}
          />
        ) : null}
      </Box>
    </Box>
  );
};

export default React.memo(LegacyReconciliationSummaryTiles);
